/**
 * App-label helper for attention items.
 *
 * Several agent tools (notably `post_review_reply`) act on a specific app, but
 * the only place that app is recorded on the attention payload is the tool-call
 * argument `args.app_id` (e.g. `app_my_timetable_ios`). That id is buried in the
 * collapsed "Tool Arguments" section, yet it's a key piece of triage context:
 * an operator wants to know *which app* a review reply is for before reading the
 * reply. This helper lifts it out so the queue row and the approval-detail
 * header can surface it prominently.
 *
 * The backend doesn't ship a human-readable name alongside the id, so we derive
 * one by stripping the `app_` prefix and the trailing platform suffix, then
 * title-casing what remains:
 *
 *   app_my_timetable_ios  ->  "My Timetable"
 *   app_healthview_ios     ->  "Healthview"
 *
 * Returns null whenever no usable app id is present, so callers can omit the
 * badge entirely rather than render an empty one — tools that don't act on an
 * app (most of them) are then visually unaffected.
 */

import type { AttentionItem } from '../types'

/** Platform suffixes we strip from an app id before humanising it. */
const PLATFORM_SUFFIXES = ['_ios', '_ipados', '_android', '_macos', '_web']

/**
 * Read the raw `app_id` from an attention item's tool-call args.
 *
 * The canonical location is `details.args.app_id`. Returns the trimmed value, or
 * null when absent / not a non-empty string.
 */
export function extractAppId(item: AttentionItem): string | null {
  const details = (item.details ?? {}) as Record<string, unknown>
  const args = isPlainObject(details.args) ? (details.args as Record<string, unknown>) : null
  const raw = args?.app_id
  if (typeof raw === 'string' && raw.trim().length > 0) return raw.trim()
  return null
}

/**
 * Convert a raw app id into a human-readable label.
 *
 * Strips a leading `app_` and any trailing platform suffix, then title-cases the
 * remaining underscore-separated tokens. Falls back to the trimmed input when
 * nothing recognisable remains, so an unexpected shape still renders something
 * meaningful rather than an empty string.
 */
export function formatAppLabel(appId: string): string {
  let slug = appId.trim()
  if (slug.length === 0) return appId

  // Drop the conventional `app_` prefix.
  if (slug.startsWith('app_')) slug = slug.slice('app_'.length)

  // Drop a single trailing platform suffix (case-insensitive).
  const lower = slug.toLowerCase()
  for (const suffix of PLATFORM_SUFFIXES) {
    if (lower.endsWith(suffix)) {
      slug = slug.slice(0, slug.length - suffix.length)
      break
    }
  }

  const label = slug
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
    .trim()

  return label.length > 0 ? label : appId.trim()
}

/**
 * Convenience: extract and format the app label for an attention item in one
 * call. Returns null when the item carries no app id.
 */
export function getAppLabel(item: AttentionItem): string | null {
  const appId = extractAppId(item)
  return appId === null ? null : formatAppLabel(appId)
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}
